import React, { useState, useEffect, useCallback, memo } from "react";
import "./EmailCaptureRating.scss";
import { FiX, FiMail } from "react-icons/fi";
import { SHOW_POPUP_EVERY } from "../../utils/constant";
import { getCookie, setCookie } from "../../utils/functions";
import { withUid } from "../../queries";

// Type definitions
declare global {
  interface Window {
    triggerEmailPopupSearch?: () => void;
  }
}

interface NewsletterPayload {
  email?: string;
  name?: string;
  rating?: number;
  comments?: string;
  "navigator.userAgent": string;
  "navigator.appName": string;
  "navigator.appVersion": string;
  "navigator.vendor": string;
  "navigator.platform": string;
}

const STORAGE_KEYS = {
  USER_EMAIL: "pw_userEmail",
  SEARCH_COUNT: "pw_searchCount",
  POPUP_OPEN: "pw_popupOpen",
} as const;

// persisted as a cookie (not localStorage): once it expires, we ask again -- re-prompting isn't immediate though,
// since SEARCH_COUNT is reset to 0 the moment this cookie is set (see submitEmail's success handler), so the popup
// only re-triggers after SHOW_POPUP_EVERY fresh searches following expiry, not on the very next search
const FEEDBACK_GIVEN_COOKIE = "pw_feedbackGiven";
const FEEDBACK_COOKIE_DAYS = 90; // ~3 months

const API_TIMEOUT = 10000;
const SUCCESS_DISPLAY_TIME = 1500;

const EmailCaptureRatingComponent: React.FC = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [comments, setComments] = useState("");
  const hasGivenFeedback = getCookie(FEEDBACK_GIVEN_COOKIE) === "true";
  useEffect(() => {
    const popupWasOpen = localStorage.getItem(STORAGE_KEYS.POPUP_OPEN) === "true";
    if (popupWasOpen && !hasGivenFeedback) setShowPopup(true);
  }, [hasGivenFeedback]);

  useEffect(() => {
    if (showPopup) localStorage.setItem(STORAGE_KEYS.POPUP_OPEN, "true");
    else localStorage.removeItem(STORAGE_KEYS.POPUP_OPEN);
  }, [showPopup]);

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const triggerEmailPopupSearch = useCallback(() => {
    if (getCookie(FEEDBACK_GIVEN_COOKIE) === "true") return;

    const currentCount = parseInt(localStorage.getItem(STORAGE_KEYS.SEARCH_COUNT) || "0", 10);
    const newCount = currentCount + 1;
    localStorage.setItem(STORAGE_KEYS.SEARCH_COUNT, String(newCount));

    if (newCount % SHOW_POPUP_EVERY === 0 ) setShowPopup(true);
  }, []);

useEffect(() => {
  window.triggerEmailPopupSearch = triggerEmailPopupSearch;
  return () => {
    window.triggerEmailPopupSearch = undefined;
  };
}, [triggerEmailPopupSearch]);


  const closePopup = useCallback(() => setShowPopup(false), []);
  const openPopup = useCallback(() => {
    // reset so a previous "Thank you!" (or a leftover error/rating/comment) doesn't linger if the user reopens
    // the popup later to submit another review -- see the floating button, which is always available post-feedback
    setSuccess(false);
    setError("");
    setRating(null);
    setComments("");
    setEmail("");
    setShowPopup(true);
  }, []);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) setError("");
  }, [error]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleCommentsChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComments(e.target.value);
  }, []);

  const submitEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (email.trim() && !isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    const payload: NewsletterPayload = {
      "navigator.userAgent": navigator.userAgent || "",
      "navigator.appName": navigator.appName || "",
      "navigator.appVersion": navigator.appVersion || "",
      "navigator.vendor": navigator.vendor || "",
      "navigator.platform": navigator.platform || "",
    };

    if (email.trim()) payload.email = email.trim();

    if (rating !== null) payload.rating = rating;

    if (comments.trim()) payload.comments = comments.trim();

    if (name.trim()) payload.name = name.trim();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const apiUrl = process.env.REACT_APP_NEWSLETTER_API ||
          "/pwserver/api/subscribe";
      const response = await fetch(withUid(apiUrl), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      switch (response.status) {
        case 200:
          setSuccess(true);
          setTimeout(() => {
            setCookie(FEEDBACK_GIVEN_COOKIE, "true", FEEDBACK_COOKIE_DAYS);
            // reset so that once the cookie above expires, re-prompting waits for SHOW_POPUP_EVERY fresh searches
            // rather than firing on the next search (which could be immediate if the count was already close to
            // a multiple of SHOW_POPUP_EVERY when feedback was given, eg. via the always-available floating button)
            localStorage.setItem(STORAGE_KEYS.SEARCH_COUNT, "0");
            if (email.trim()) localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email.trim());
            localStorage.removeItem(STORAGE_KEYS.POPUP_OPEN);
            setShowPopup(false);
          }, SUCCESS_DISPLAY_TIME);
          break;

        case 400:
          setError("Invalid email address. Please check and try again.");
          break;

        case 409:
          setError("This email is already subscribed.");
          break;

        default:
          setError(`Submission failed (${response.status}). Please try again.`);
      }
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === "AbortError") setError("Request timed out. Please try again.");
        else setError(`Network error: ${err.message}. Please try again.`);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  return (
    <>
      {!showPopup && (
        <button
          onClick={openPopup}
          className="email-float-btn"
          aria-label="Open newsletter subscription"
          title="Get Laptop Deals"
          type="button"
        >
          <FiMail size={28} />
        </button>
      )}

      {showPopup && (
        <div className="email-popup-container" role="dialog" aria-labelledby="popup-title">
          <div className="email-popup">
            <button onClick={closePopup} className="close-btn" aria-label="Close popup" type="button">
              <FiX size={22} />
            </button>

            <div className="popup-content">
              {/*}<div className="icon">
                <FiMail size={36} />
              </div>*/}
              <p className="heading">Feedback please!</p>
              {success ? (
                <div className="success-message">
                  ✓ Thank you!
                </div>
              ) : (
                <form onSubmit={submitEmail} noValidate>
                  {/*<div className="form-group">*/}
                  {/*  <label htmlFor="name-input">Name</label>*/}
                  {/*  <input*/}
                  {/*    id="name-input"*/}
                  {/*    type="text"*/}
                  {/*    placeholder="Enter your name"*/}
                  {/*    value={name}*/}
                  {/*    onChange={handleNameChange}*/}
                  {/*    disabled={loading}*/}
                  {/*    autoComplete="name"*/}
                  {/*  />*/}
                  {/*</div>*/}

                  <div className="form-group survey-group">
                    <label>How would you rate us!</label>

                    <div className="rating-slider" role="radiogroup" aria-label="Rate us from 1 to 5">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <label
                          key={value}
                          className={`rating-option${rating === value ? " selected" : ""}`}
                        >
                          <input
                            type="radio"
                            name="rating"
                            value={value}
                            checked={rating === value}
                            onChange={() => setRating(value)}
                            disabled={loading}
                          />
                          <span>{value}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="comments-input">What do you think about PriceWired?</label>
                    <textarea
                      id="comments-input"
                      placeholder="Tell us what you think..."
                      value={comments}
                      onChange={handleCommentsChange}
                      disabled={loading}
                      rows={2}
                      maxLength={350}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email-input">Email</label>
                    <input
                        id="email-input"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={handleEmailChange}
                        disabled={loading}
                        autoComplete="email"
                        aria-invalid={!!error}
                    />
                  </div>

                  <button type="submit" disabled={loading} className="submit-btn">
                    {loading ? (
                      <>
                        <span className="spinner"></span> Sending...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>

                  {error && <p className="error">⚠ {error}</p>}
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export const EmailCaptureRating = memo(EmailCaptureRatingComponent);
